import { ChevronLeft, Mail, MapPin, Phone, Search, Star } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'

interface AgentSearchScreenProps {
  onBack: () => void
}

const demoAgents = [
  {
    id: 1,
    name: 'Sarah Mitchell',
    company: 'Ray White Remuera',
    area: 'Auckland Central, Remuera, Epsom',
    rating: 4.9,
    reviews: 124,
    sales: 87,
    speciality: 'Residential',
    phone: '021 555 0123',
    email: 'sarah@example.com',
    photo: 'S',
    yearsExperience: 12,
  },
  {
    id: 2,
    name: 'James Wong',
    company: 'Harcourts Wellington',
    area: 'Wellington CBD, Thorndon, Karori',
    rating: 4.8,
    reviews: 98,
    sales: 65,
    speciality: 'Apartments & Units',
    phone: '027 555 0456',
    email: 'james@example.com',
    photo: 'J',
    yearsExperience: 8,
  },
  {
    id: 3,
    name: 'Emma Thompson',
    company: 'Bayleys Canterbury',
    area: 'Christchurch, Merivale, Fendalton',
    rating: 4.9,
    reviews: 156,
    sales: 92,
    speciality: 'Luxury Homes',
    phone: '022 555 0789',
    email: 'emma@example.com',
    photo: 'E',
    yearsExperience: 15,
  },
  {
    id: 4,
    name: 'David Chen',
    company: 'LJ Hooker Hamilton',
    area: 'Hamilton, Cambridge, Te Awamutu',
    rating: 4.7,
    reviews: 67,
    sales: 54,
    speciality: 'Rural & Lifestyle',
    phone: '021 555 0321',
    email: 'david@example.com',
    photo: 'D',
    yearsExperience: 6,
  },
  {
    id: 5,
    name: 'Lisa Patel',
    company: 'Barfoot & Thompson',
    area: 'North Shore, Takapuna, Devonport',
    rating: 4.8,
    reviews: 89,
    sales: 71,
    speciality: 'Residential',
    phone: '027 555 0654',
    email: 'lisa@example.com',
    photo: 'L',
    yearsExperience: 10,
  },
]

export function AgentSearchScreen({ onBack }: AgentSearchScreenProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedAgent, setSelectedAgent] = useState<(typeof demoAgents)[0] | null>(null)

  const filteredAgents = demoAgents.filter(
    (agent) =>
      agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.area.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.company.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  if (selectedAgent) {
    return (
      <div className="flex flex-col min-h-full pb-4">
        <button
          onClick={() => setSelectedAgent(null)}
          className="flex items-center gap-2 text-primary mb-4 hover:underline"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to search
        </button>

        <Card className="p-6 mb-4">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-2xl font-semibold">
              {selectedAgent.photo}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-foreground">{selectedAgent.name}</h2>
              <p className="text-sm text-muted-foreground">{selectedAgent.company}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="flex items-center gap-1 text-amber-500 text-sm">
                  <Star className="w-4 h-4 fill-current" />
                  {selectedAgent.rating}
                </span>
                <span className="text-xs text-muted-foreground">
                  ({selectedAgent.reviews} reviews)
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 py-4 border-y border-border mb-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-foreground">{selectedAgent.sales}</p>
              <p className="text-xs text-muted-foreground">Sales</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-foreground">{selectedAgent.yearsExperience}</p>
              <p className="text-xs text-muted-foreground">Years Exp.</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-foreground">{selectedAgent.reviews}</p>
              <p className="text-xs text-muted-foreground">Reviews</p>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Areas Covered</p>
              <p className="text-sm text-foreground flex items-center gap-1">
                <MapPin className="w-4 h-4 text-primary" />
                {selectedAgent.area}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Speciality</p>
              <p className="text-sm text-foreground">{selectedAgent.speciality}</p>
            </div>
          </div>

          <div className="space-y-2">
            <Button className="w-full h-12" size="lg">
              <Phone className="w-4 h-4 mr-2" />
              Call {selectedAgent.phone}
            </Button>
            <Button variant="outline" className="w-full h-12" size="lg">
              <Mail className="w-4 h-4 mr-2" />
              Send Email
            </Button>
          </div>
        </Card>

        <Card className="p-4 bg-secondary/50">
          <p className="text-xs text-muted-foreground text-center">
            All REINZ member agents are licensed under the Real Estate Agents Act 2008 and adhere to
            the REINZ Code of Conduct.
          </p>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-full pb-4">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-primary mb-4 hover:underline"
      >
        <ChevronLeft className="w-4 h-4" />
        Back
      </button>

      <h1 className="text-xl font-semibold text-foreground mb-4">Find an Agent</h1>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search by name, area, or agency..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 h-12"
        />
      </div>

      <p className="text-sm text-muted-foreground mb-4">{filteredAgents.length} agents found</p>

      <div className="space-y-3">
        {filteredAgents.map((agent) => (
          <Card
            key={agent.id}
            className="p-4 cursor-pointer card-interactive"
            onClick={() => setSelectedAgent(agent)}
          >
            <div className="flex items-start gap-3">
              <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold text-lg">
                {agent.photo}
              </div>
              <div className="flex-1">
                <p className="font-medium text-foreground">{agent.name}</p>
                <p className="text-xs text-muted-foreground mb-1">{agent.company}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {agent.area}
                </p>
                <div className="flex items-center gap-3 mt-2">
                  <span className="flex items-center gap-1 text-amber-500 text-sm">
                    <Star className="w-3 h-3 fill-current" />
                    {agent.rating}
                  </span>
                  <span className="text-xs text-muted-foreground">{agent.sales} sales</span>
                  <span className="text-xs text-muted-foreground">
                    {agent.yearsExperience} yrs exp
                  </span>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  )
}
